/**
 * Utils and top level globals occupy the root appn name space.
 * Contains common functions and constants which are accessible anywhere via appn DOT notation.
 * If a function is repeated 3 or more times then it should live here and then just called from the 3 places its needed.
 */
namespace appn.utils {

    export const getKeyValue = <U extends keyof T, T extends object>(key: U) => (obj: T) => obj[key];
    export const getKeyValue2 = <T extends object, U extends keyof T>(obj: T) => (key: U) => obj[key];

    /**
     * A handy generic has value check
     * Checks all datatypes and jquery selector for a valid value.
     * Note this is not the same as truthy or falsey.
     */
    export function hasValue(param: any): boolean {
        if (typeof param === 'number' && param === 0)
            return true;

        if (param instanceof jQuery)
            return $(param).length > 0;

        if (typeof param === 'object')
            return !$.isEmptyObject(param);

        if (typeof param === 'undefined')
            return false;

        return !(!param);
    }
    
    export function hasValues(...params: Array<any>){
        if (!params || params.length === 0)
            return false;
        
        return _.every(params, utils.hasValue);
    }
    
    export function isIn(value: any, ...inValues: any[]): boolean {
        if (!hasValue(value) || !hasValue(inValues))
            return false;
        return _.some(inValues, v => v === value);
    }

    /**
     * Returns true if the string contains a whole number (int)
     * will return false for any other type
     */
    export function isWholeNumber(value: string) {
        return /^-{0,1}\d+$/.test(value);
    }

    /**
     * Validates an email address
     * @param emailAddress
     */
    export function isValidEmailAddress(emailAddress: string): boolean {
        const pattern = /^([a-z\d!#$%&'*+\-\/=?^_`{|}~\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]+(\.[a-z\d!#$%&'*+\-\/=?^_`{|}~\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]+)*|"((([ \t]*\r\n)?[ \t]+)?([\x01-\x08\x0b\x0c\x0e-\x1f\x7f\x21\x23-\x5b\x5d-\x7e\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]|\\[\x01-\x09\x0b\x0c\x0d-\x7f\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))*(([ \t]*\r\n)?[ \t]+)?")@(([a-z\d\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]|[a-z\d\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF][a-z\d\-._~\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]*[a-z\d\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])\.)+([a-z\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]|[a-z\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF][a-z\d\-._~\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]*[a-z\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])\.?$/i;
        return pattern.test(emailAddress);
    }

    /**
     * Creates a new Guid
     */
    export function newGuid(excludeDashes?: boolean): string {
        const s4 = function() {
            return Math.floor((1 + Math.random()) * 0x10000)
                .toString(16)
                .substring(1);
        };

        if (excludeDashes)
            return s4() + s4() + s4() + s4() + s4() + s4() + s4() + s4();

        return s4() + s4() + '-' + s4() + '-' + s4() + '-' + s4() + '-' + s4() + s4() + s4();
    }

    /**
     * Creates a new Id
     */
    export function newUniqueId(): string {
        const guid = newGuid(true);

        return guid.substring(0, 3);
    }

    /**
     * Normalizes an identifier to be case-insensitive and ignores hyphens.
     * This ensures consistency between client-generated IDs and server-provided IDs.
     * @param id The identifier string to normalize.
     */
    export function normalizeIdentifier(id: string): string {
        if (!id)
            return '';

        return id.toLowerCase().replace(/-/g, '');
    }

    /**
     * Array Remove - Adapted from John Resig (MIT Licensed)
     * @param array
     * @param from
     * @param to
     */
    export function arrayRemove(array: Array<any>, from: number, to?: number): Array<any> {
        const rest = array.slice((to || from) + 1 || array.length);
        array.length = from < 0 ? array.length + from : from;
        return array.push.apply(array, rest) as any;
    }

    /**
     * Reliably get params from the Url query string
     * @param name
     * @param url
     */
    export function getUrlParamByName(name: string, url?: string): string | null {
        if (!url)
            url = window.location.href;

        name = name.replace(/[\[\]]/g, '\\$&');
        const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
            results = regex.exec(url);
        if (!results) return null;
        if (!results[2]) return appn.emptyString;
        return decodeURIComponent(results[2].replace(/\+/g, ' '));
    }

    export function isValidFileExtension(validExtensions: Array<string>, fileName: string): boolean {
        if (!fileName || !validExtensions || validExtensions.length === 0)
            return false;

        let isValidExtension = false;
        $(validExtensions).each((function(ii: number, fileExtension: string): boolean {
            if (fileName.trim().toLowerCase().endsWithIgnoreCase(fileExtension.toLowerCase())) {
                isValidExtension = true;
                return false; // break out of this loop
            }

            return true;
        } as any));

        if (!isValidExtension) {
            dialog.alert({
                modalColor: 'modal-danger',
                title: T('Invalid File Format'),
                message: T('{0} is not allowed.<br>Only the following file extensions are permitted:', fileName) + '<br><b>' + validExtensions.join(',') + '</b>'
            });
            return false;
        }

        return true;
    }

    export function IsValidFileSize(maxAllowedFileSize: number, file: any): boolean {
        if (!maxAllowedFileSize || !file)
            return false;

        const sizeKb = (file.size || file.fileSize) / 1024;
        if (sizeKb > maxAllowedFileSize) {
            dialog.alert({
                modalColor: 'modal-danger',
                title: T('Max File Size Exceeded'),
                message: T('The maximum image size allowed is {0} kilobytes. Use an image editor to resize your image, and please ensure that the replacement icon image is 192 x 192 pixels in dimension.', maxAllowedFileSize.toString())
            });
            return false;
        }

        return true;
    }

    export function deepClone<T>(obj: T): T {
        if (obj === null || typeof obj !== 'object')
            return obj;

        if (Array.isArray(obj)) {
            const arrCopy = [] as any[];
            for (let i = 0; i < obj.length; i++)
                arrCopy[i] = deepClone(obj[i]);

            return arrCopy as T;
        }

        const objCopy = {} as T;
        for (const key in obj) {
            if (obj.hasOwnProperty(key))
                (objCopy as any)[key] = deepClone((obj as any)[key]);
        }

        return objCopy as T;
    }
    
    export function cloneTemplate(templateId: string, throwIfNotFound: boolean = true): JQuery<HTMLElement> {
        const templateElement = document.getElementById(templateId) as HTMLTemplateElement;
        if (templateElement)
            return $(templateElement.content.cloneNode(true)).children() as JQuery<HTMLElement>;
        
        if (throwIfNotFound)
            throw new Error(`Template not found for id: ${templateId}`);
        
        return $();
    }

    /**
     * Returns true if a > b for Time or DateTime values
     * Returns undefined if either a or b are malformed
     * @param a
     * @param b
     */
    export function isGreaterThanTimeOrDate(a: string, b: string): boolean {
        // Parse values
        let parsedA = new Date(a).getTime();
        let parsedB = new Date(b).getTime();

        if (!Number.isNaN(parsedA) && !Number.isNaN(parsedB))
            return parsedA > parsedB;

        parsedA = (new Date('1970-01-01 ' + a)).getTime();
        parsedB = (new Date('1970-01-01 ' + b)).getTime();

        if (!Number.isNaN(parsedA) && !Number.isNaN(parsedB))
            return parsedA > parsedB;

        log.error(`Time or dateTime inputs a: ${a} or b: ${b} were malformed`, true);
        return false;
    }

    export function isValidUrl(val?: string): boolean {
        if (!hasValue(val))
            return false;

        let url;
        try {
            url = new URL(val as string);
        } catch (_) {
            return false;
        }

        return url.protocol === 'http:' || url.protocol === 'https:';
    }

    export function isValidMapTileSourceUrl(val?: string): boolean {
        if (!hasValue(val))
            return false;

        if (!val?.startsWithIgnoreCase('http'))
            return true;

        if (!val?.contains('{z}') || !val?.contains('{x}') || !val?.contains('{y}'))
            return false;

        const urlClean = val.replace('{', '').replace('}', '');

        return isValidUrl(urlClean);
    }
    
    export function upperCaseFirst(input: string | null | undefined): string {
        if (!input || input.length === 0)
            return appn.emptyString;
        
        return input.charAt(0).toUpperCase() + input.slice(1)
        
    } 

    export function shake(element: JQuery<HTMLElement>) {
        element.addClass('headshake');
        setTimeout(function () {
            element.removeClass('headshake');
        }, 820);
    }

    export function MD5Unicode(buffer: any): string {
        var MD5 = function(d: string) {
            return M(V(Y(X(d), 8 * d.length)));
        };
        function M(d: string) {
            for (var _, m = '0123456789abcdef', f = '', r = 0; r < d.length; r++) {
                _ = d.charCodeAt(r);
                f += m.charAt(_ >>> 4 & 15) + m.charAt(15 & _);
            }
            return f;
        }
        function X(d: string) {
            for (var _ = Array(d.length >> 2), m = 0; m < _.length; m++) {
                _[m] = 0;
            }
            for (m = 0; m < 8 * d.length; m += 8) {
                _[m >> 5] |= (255 & d.charCodeAt(m / 8)) << m % 32;
            }
            return _;
        }
        function V(d: any) {
            for (var _ = '', m = 0; m < 32 * d.length; m += 8) _ += String.fromCharCode(d[m >> 5] >>> m % 32 & 255);
            return _;
        }
        function Y(d: any, _: number) {
            d[_ >> 5] |= 128 << _ % 32;
            d[14 + (_ + 64 >>> 9 << 4)] = _;
            for (var m = 1732584193, f = -271733879, r = -1732584194, i = 271733878, n = 0; n < d.length; n += 16) {
                var h = m;
                var t = f;
                var g = r;
                var e = i;
                f = md5ii(f = md5ii(f = md5ii(f = md5ii(f = md5hh(f = md5hh(f = md5hh(f = md5hh(f = md5gg(f = md5gg(f = md5gg(f = md5gg(f = md5ff(f = md5ff(f = md5ff(f = md5ff(f, r = md5ff(r, i = md5ff(i, m = md5ff(m, f, r, i, d[n + 0], 7, -680876936), f, r, d[n + 1], 12, -389564586), m, f, d[n + 2], 17, 606105819), i, m, d[n + 3], 22, -1044525330), r = md5ff(r, i = md5ff(i, m = md5ff(m, f, r, i, d[n + 4], 7, -176418897), f, r, d[n + 5], 12, 1200080426), m, f, d[n + 6], 17, -1473231341), i, m, d[n + 7], 22, -45705983), r = md5ff(r, i = md5ff(i, m = md5ff(m, f, r, i, d[n + 8], 7, 1770035416), f, r, d[n + 9], 12, -1958414417), m, f, d[n + 10], 17, -42063), i, m, d[n + 11], 22, -1990404162), r = md5ff(r, i = md5ff(i, m = md5ff(m, f, r, i, d[n + 12], 7, 1804603682), f, r, d[n + 13], 12, -40341101), m, f, d[n + 14], 17, -1502002290), i, m, d[n + 15], 22, 1236535329), r = md5gg(r, i = md5gg(i, m = md5gg(m, f, r, i, d[n + 1], 5, -165796510), f, r, d[n + 6], 9, -1069501632), m, f, d[n + 11], 14, 643717713), i, m, d[n + 0], 20, -373897302), r = md5gg(r, i = md5gg(i, m = md5gg(m, f, r, i, d[n + 5], 5, -701558691), f, r, d[n + 10], 9, 38016083), m, f, d[n + 15], 14, -660478335), i, m, d[n + 4], 20, -405537848), r = md5gg(r, i = md5gg(i, m = md5gg(m, f, r, i, d[n + 9], 5, 568446438), f, r, d[n + 14], 9, -1019803690), m, f, d[n + 3], 14, -187363961), i, m, d[n + 8], 20, 1163531501), r = md5gg(r, i = md5gg(i, m = md5gg(m, f, r, i, d[n + 13], 5, -1444681467), f, r, d[n + 2], 9, -51403784), m, f, d[n + 7], 14, 1735328473), i, m, d[n + 12], 20, -1926607734), r = md5hh(r, i = md5hh(i, m = md5hh(m, f, r, i, d[n + 5], 4, -378558), f, r, d[n + 8], 11, -2022574463), m, f, d[n + 11], 16, 1839030562), i, m, d[n + 14], 23, -35309556), r = md5hh(r, i = md5hh(i, m = md5hh(m, f, r, i, d[n + 1], 4, -1530992060), f, r, d[n + 4], 11, 1272893353), m, f, d[n + 7], 16, -155497632), i, m, d[n + 10], 23, -1094730640), r = md5hh(r, i = md5hh(i, m = md5hh(m, f, r, i, d[n + 13], 4, 681279174), f, r, d[n + 0], 11, -358537222), m, f, d[n + 3], 16, -722521979), i, m, d[n + 6], 23, 76029189), r = md5hh(r, i = md5hh(i, m = md5hh(m, f, r, i, d[n + 9], 4, -640364487), f, r, d[n + 12], 11, -421815835), m, f, d[n + 15], 16, 530742520), i, m, d[n + 2], 23, -995338651), r = md5ii(r, i = md5ii(i, m = md5ii(m, f, r, i, d[n + 0], 6, -198630844), f, r, d[n + 7], 10, 1126891415), m, f, d[n + 14], 15, -1416354905), i, m, d[n + 5], 21, -57434055), r = md5ii(r, i = md5ii(i, m = md5ii(m, f, r, i, d[n + 12], 6, 1700485571), f, r, d[n + 3], 10, -1894986606), m, f, d[n + 10], 15, -1051523), i, m, d[n + 1], 21, -2054922799), r = md5ii(r, i = md5ii(i, m = md5ii(m, f, r, i, d[n + 8], 6, 1873313359), f, r, d[n + 15], 10, -30611744), m, f, d[n + 6], 15, -1560198380), i, m, d[n + 13], 21, 1309151649), r = md5ii(r, i = md5ii(i, m = md5ii(m, f, r, i, d[n + 4], 6, -145523070), f, r, d[n + 11], 10, -1120210379), m, f, d[n + 2], 15, 718787259), i, m, d[n + 9], 21, -343485551);
                m = safeadd(m, h);
                f = safeadd(f, t);
                r = safeadd(r, g);
                i = safeadd(i, e);
            }
            return [m, f, r, i];
        }
        function md5cmn(d: number, _: any, m: any, f: any, r: number, i: any) {
            return safeadd(bitrol(safeadd(safeadd(_, d), safeadd(f, i)), r), m);
        }
        function md5ff(d: number, _: number, m: number, f: number, r: any, i: number, n: number) {
            return md5cmn(_ & m | ~_ & f, d, _, r, i, n);
        }
        function md5gg(d: number, _: number, m: number, f: number, r: any, i: number, n: number) {
            return md5cmn(_ & f | m & ~f, d, _, r, i, n);
        }
        function md5hh(d: number, _: number, m: number, f: number, r: any, i: number, n: number) {
            return md5cmn(_ ^ m ^ f, d, _, r, i, n);
        }
        function md5ii(d: number, _: number, m: number, f: number, r: any, i: number, n: number) {
            return md5cmn(m ^ (_ | ~f), d, _, r, i, n);
        }
        function safeadd(d: number, _: number) {
            var m = (65535 & d) + (65535 & _);
            return (d >> 16) + (_ >> 16) + (m >> 16) << 16 | 65535 & m;
        }
        function bitrol(d: number, _: number) {
            return d << _ | d >>> 32 - _;
        }

        if (!(buffer instanceof Uint8Array)) {
            buffer = new TextEncoder().encode(typeof buffer === 'string' ? buffer : JSON.stringify(buffer));
        }
        var binary = Array<string>();
        var bytes = new Uint8Array(buffer);
        for (var i = 0, il = bytes.byteLength; i < il; i++) {
            binary.push(String.fromCharCode(bytes[i]));
        }
        return MD5(binary.join(''));
    };

    export type HexColor = string;

    export class RgbColor {
        readonly red: number;
        readonly green: number;
        readonly blue: number;

        constructor(red: number, green: number, blue: number) {
            this.red = red;
            this.green = green;
            this.blue = blue;
        }

        toString(): string {
            return `rgb(${this.red}, ${this.green}, ${this.blue});`;
        }
    }

    export class ColorPalette {

        readonly white: Color;
        readonly greyLight: Color;
        readonly grey: Color;
        readonly black: Color;
        readonly blackDark: Color;

        constructor() {
            this.white = {
                hex: '#FFFFFF',
                rgb: new RgbColor(255, 255, 255)
            };

            this.greyLight = {
                hex: '#EEEEEE',
                rgb: new RgbColor(238, 238, 238)
            };

            this.grey = {
                hex: '#7B7B7B',
                rgb: new RgbColor(123, 123, 123)
            };

            this.black = {
                hex: '#333333',
                rgb: new RgbColor(51, 51, 51)
            };

            this.blackDark = {
                hex: '#222222',
                rgb: new RgbColor(34, 34, 34)
            };
        }
    }

    export interface Color {
        hex: HexColor;
        rgb: RgbColor;
    }

    // Whether a hex color reads as light, so a contrasting text/stripe color can be picked.
    // Mirrors ColorUtils.IsLightColor (Appenate.Common/Utils/DisplayUtils.cs) - same perceived
    // brightness formula and threshold - so the client and server agree on what counts as light.
    export function isLightColor(hex: string): boolean {
        if (!hex || hex === 'transparent')
            return false;

        let value = hex.replace('#', '');
        if (value.length === 3)
            value = value.split('').map(c => c + c).join('');

        const r = parseInt(value.substring(0, 2), 16) || 0;
        const g = parseInt(value.substring(2, 4), 16) || 0;
        const b = parseInt(value.substring(4, 6), 16) || 0;

        const brightness = Math.sqrt(r * r * 0.299 + g * g * 0.587 + b * b * 0.114);
        return brightness > 165;
    }

    // Register String extensions START
    String.prototype.contains = function(stringToFind: string): boolean {
        return this.indexOf(stringToFind) !== -1;
    };

    String.prototype.containsIgnoreCase = function(stringToFind: string): boolean {
        return this.toLowerCase().indexOf(stringToFind.toLowerCase()) !== -1;
    };

    String.prototype.endsWithIgnoreCase = function(stringToFind: string, dontTrim: boolean): boolean {
        if (dontTrim)
            return this.toLowerCase().endsWith(stringToFind.toLowerCase());

        return this.trim().toLowerCase().endsWith(stringToFind.trim().toLowerCase());
    };

    String.prototype.startsWithIgnoreCase = function(stringToFind: string, dontTrim: boolean): boolean {
        if (dontTrim)
            return this.toLowerCase().startsWith(stringToFind.toLowerCase());

        return this.trim().toLowerCase().startsWith(stringToFind.trim().toLowerCase());
    };

    String.prototype.hasValue = function(): boolean {
        if (this === null || this === undefined)
            return false;

        return this.trim() !== appn.emptyString;
    };

    String.prototype.AsJSON = function(): any {
        if (this === null || this === undefined)
            return {};

        return JSON.parse(this.toString());
    };

    String.prototype.IsJSON = function(): boolean {
        try {
            if (this.toString())
                JSON.parse(this.toString());
            else
                return false;
        } catch (e) {
            return false;
        }

        return true;
    };

    String.prototype.toHtmlElement = function(): HTMLElement {
        const dom = document.createElement('div');
        dom.innerHTML = this.toString();
        return dom;
    };

    /**
     * Returns undefined if the string can't parse to boolean
     */
    String.prototype.toBoolean = function(this: string) {
        if (this.toLowerCase() === 'true')
            return true;

        if (this.toLowerCase() === 'false')
            return false;
    };

    /**
     * Returns false if the string can't parse to boolean
     */
    String.prototype.toBooleanExact = function(this: string) {
        if (this.toLowerCase() === 'true')
            return true;

        if (this.toLowerCase() === 'false')
            return false;

        return false;
    };

    // Register String extensions END

    // Register Array extensions START

    /**
     * Returns a distinct array
     */
    $.distinct = function<T>(array: Array<T>) {
        return array.filter((o, i, s) => {
            return s.indexOf(o) === i;
        });
    };

    $.arrayContains = function <T>(array: Array<T>, value: T) {
        if (array.indexOf(value) > -1)
            return true;

        return false;
    };
    // Register Array extensions END

    // Register JQuery extensions START
    /**
     * Adds class="d-none" to element without adding duplicates, and removes additional whitespace from class
     * Will preserve 'd-' classes in the data-oldclasses attribute on the element
     * 
     * This is a JQuery extension so when calling this method, ensure that your selector is returning the correct element, or range of elements you want to hide.
     */
    $.fn.hideElement = function() {
        if (!this)
            return this;

        this.each((index: number, element: HTMLElement) => {
            const $this = $(element);

            if (!$this.attr('class'))
                return;

            let oldclasses: Array<string> = [];
            let oldDisplayClasses: Array<string> = [];
            if ($this.data('oldclasses')) {
                oldclasses = $this.data('oldclasses').split(' ').filter((oldClassItem: string) => {
                    return (hasValue(oldClassItem) && oldClassItem !== 'd-none' && !oldClassItem.startsWithIgnoreCase('d-'));
                }); // remove possible blanks
                oldDisplayClasses = $this.data('oldclasses').split(' ').filter((oldClassItem: string) => {
                    return (hasValue(oldClassItem) && oldClassItem !== 'd-none' && oldClassItem.startsWithIgnoreCase('d-'));
                });
            }

            const classes = ($this.attr('class') as string).split(' ').filter((classItem: string) => {
                return (hasValue(classItem) && classItem !== 'd-none' && !classItem.startsWithIgnoreCase('d-'));
            }); // remove possible blanks
            const displayClasses = ($this.attr('class') as string).split(' ').filter((classItem: string) => {
                return (hasValue(classItem) && classItem !== 'd-none' && classItem.startsWithIgnoreCase('d-'));
            });

            const newClasses = _.union(oldclasses, classes); // returns the join of the two arrays without duplicates
            const newDisplayClasses = _.union(oldDisplayClasses, displayClasses); // returns the join of the two arrays without duplicates
            if (newDisplayClasses.length > 0) {
                $this.attr('data-oldclasses', newDisplayClasses.join(' '));
                newDisplayClasses.forEach((cls: string) => {
                    $this.removeClass(cls); // remove the preserved classes only
                });
            }

            $this.attr('class', newClasses.join(' ') + ' d-none');
        });

        return this;
    };

    /**
     * Removes class="d-none" to element, and removes additional whitespace from classes
     * Will re-add preserved 'd-' classes by hideElement() to the element
     *
     * This is a JQuery extension so when calling this method, ensure that your selector is returning the correct element, or range of elements you want to hide.
     */
    $.fn.showElement = function() {
        if (!this)
            return this;

        this.each((index: number, element: HTMLElement) => {
            const $this = $(element);
            if (!$this.attr('class'))
                return;

            let oldDisplayclasses: Array<string> = [];
            if ($this.data('oldclasses'))
                oldDisplayclasses = $this.data('oldclasses').split(' ').filter((oldClassItem: string) => {
                    return hasValue(oldClassItem);
                }); // remove possible blanks

            let classes = ($this.attr('class') as string).split(' ').filter((classItem: string) => {
                return hasValue(classItem);
            });
            classes = _.union(classes, oldDisplayclasses); // preserve any old and new classes

            $this.attr('class', $.distinct<string>(classes.filter((o) => {
                return o !== 'd-none';
            })).join(' '));
            $this.removeAttr('data-oldclasses');
        });

        return this;
    };

    // Register JQuery extensions END

    // Add hook and handler for global DOM events START
    export let isUnloading: boolean = false;
    export let isAppnScriptLoaded: boolean = false;
    export let mapLoadState: string = "";

    addEventListener('unload', () => {
        isUnloading = true;
    });

    pubSub.subscribe(pubSub.APPN_READY, () => {
        // APPN_READY subscribers (e.g. view init, which dispatches the page's data requests)
        // run in the async pubsub tick after this publish. Defer the flag one tick so it only
        // flips once those requests have been dispatched (not necessarily completed).
        setTimeout(() => {
            isAppnScriptLoaded = true;
        }, 0);
    })
}

namespace appn.validate {
    export function init() : void {
        $(document).on('change', '.input-group[data-validationstate] .editorTextfield', (e: Event) => {
            $(e.target!).closest('.input-group').attr('data-validationstate', 'edited')
        });
    }
}

/**
 * String extensions
 */
interface String {
    contains(stringToFind: string): boolean;
    containsIgnoreCase(stringToFind: string): boolean;
    endsWithIgnoreCase(stringToFind: string, dontTrim?: boolean): boolean;
    startsWithIgnoreCase(stringToFind: string, dontTrim?: boolean): boolean;
    endsWith(searchString: string, endPosition?: number): boolean;
    startsWith(searchString: string, startPosition?: number): boolean;
    hasValue(): boolean;
    AsJSON(): any;
    IsJSON(): boolean;
    toBoolean: (this: string) => boolean | undefined;
    toBooleanExact: (this: string) => boolean;
    toHtmlElement(): HTMLElement;
}

/**
 * Array<T> extensions
 */
interface JQueryStatic {
    [x: string]: any;
    distinct<T>(array: Array<T>): Array<T>;
    arrayContains<T>(array: Array<T>, value: T): boolean;
}

/**
 * JQuery extensions
 */
interface JQuery {
    smallipop(arg0: { preferredPosition: string; theme: string; popupOffset: number; }): unknown;
    hideElement(this: JQuery<HTMLElement>): JQuery<HTMLElement>;
    showElement(this: JQuery<HTMLElement>): JQuery<HTMLElement>;
}
